import { Box, useMediaQuery } from '@mui/material';
import { Typography } from '@sm-dashboard/ui-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useSwipe } from './useSwipe';
import { ViewAllAdsButton } from './ViewAllAdsButton';
import BusinessAdCard from '@/components/dashboard/BusinessAds/BusinessAdCard';
import {
  BusinessAd,
  addFavorite,
  removeFavorite,
  checkMultipleFavorites
} from '@/components/dashboard/BusinessAds/businessAdsUtils';
import { theme } from '@/theme/theme';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';

interface BusinessAdsCarouselProps {
  ads: BusinessAd[];
}

export const BusinessAdsCarousel = ({ ads }: BusinessAdsCarouselProps) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const { currentIndex, goToIndex, handleDragEnd } = useSwipe({
    itemCount: ads?.length || 0,
    isMobile
  });

  // Fetch user favorites when ads change
  useEffect(() => {
    if (ads && ads.length > 0) {
      const adIds = ads.map((ad: BusinessAd) => ad._id);
      const fetchUserFavorites = async () => {
        try {
          const favoritesMap = await checkMultipleFavorites(adIds);
          const userFavorites = new Set<string>();
          Object.entries(favoritesMap).forEach(([adId, isFavorited]) => {
            if (isFavorited) {
              userFavorites.add(adId);
            }
          });
          setFavorites(userFavorites);
        } catch (error) {
          console.error('Error fetching user favorites:', error);
        }
      };
      fetchUserFavorites();
    }
  }, [ads]);

  const handleCardClick = useCallback(
    (ad: BusinessAd) => {
      navigate(`/tools/business-ads/${ad._id}`);
    },
    [navigate]
  );

  const handleToggleFavorite = useCallback(
    async (adId: string) => {
      const isFavorited = favorites.has(adId);
      try {
        if (isFavorited) {
          const success = await removeFavorite(adId);
          if (success) {
            setFavorites(prev => {
              const newFavorites = new Set(prev);
              newFavorites.delete(adId);
              return newFavorites;
            });
          }
        } else {
          const success = await addFavorite(adId);
          if (success) {
            setFavorites(prev => {
              const newFavorites = new Set(prev);
              newFavorites.add(adId);
              return newFavorites;
            });
          }
        }
      } catch (error) {
        console.error('Error toggling favorite:', error);
      }
    },
    [favorites]
  );

  if (!ads || ads.length === 0) {
    return null;
  }

  const currentAd = ads[currentIndex];

  return (
    <Box sx={{ width: '100%', mb: 3, pt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, px: 1 }}>
        <Typography variant="h2">מודעות מתוך לוח המפרסמים</Typography>
        <ViewAllAdsButton />
      </Box>

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '600px',
            position: 'relative',
            overflow: 'hidden',
            flex: 1
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: isMobile ? 30 : 100 }}
              animate={{
                opacity: 1,
                x: 0,
                transition: {
                  type: 'spring',
                  stiffness: 300,
                  damping: 30
                }
              }}
              exit={{ opacity: 0, x: isMobile ? -30 : -100 }}
              drag={isMobile ? 'x' : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.3}
              onDragEnd={handleDragEnd}
              style={{
                width: '100%',
                touchAction: 'pan-y pinch-zoom',
                userSelect: 'none'
              }}
            >
              <Box sx={{ width: '100%' }}>
                <BusinessAdCard
                  ad={currentAd}
                  isFavorited={favorites.has(currentAd._id)}
                  commentCount={0}
                  ratingData={{ averageRating: 0, totalRatings: 0 }}
                  onToggleFavorite={handleToggleFavorite}
                  onCardClick={handleCardClick}
                />
              </Box>
            </motion.div>
          </AnimatePresence>
        </Box>
      </Box>

      {ads.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            mt: 2
          }}
        >
          {ads.map((_, index) => (
            <Box
              key={index}
              onClick={() => goToIndex(index)}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: index === currentIndex ? 'primary.main' : 'action.disabled',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
